"use client";

import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import { getStrapiMedia } from "@/utils/api-helpers";


declare global {
  interface Window {
    innerSwipers: any[];
  }
}

function getOriginalIndexFromSlideEl(slideEl: HTMLElement | null): number | null {
  if (!slideEl) return null;
  const ds = slideEl.getAttribute('data-swiper-slide-index');
  if (ds !== null) return Number(ds);
  const d = slideEl.getAttribute('data-index');
  return d !== null ? Number(d) : null;
}

export default function TiktokSlider({ slides }: { slides?: any }) {
  const outerSwiperRef = useRef<any>(null);
  const videoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeVideos, setActiveVideos] = useState<{ [key: number]: number }>({});
  const [isMuted, setIsMuted] = useState<{ [key: string]: boolean }>(() => {
    const initialMutedState: { [key: string]: boolean } = {};
    slides?.forEach((slide: any) => {
      slide.videos?.forEach((video: any, index: number) => {
        const videoKey = `${slide.id}-${index}`;
        initialMutedState[videoKey] = true; 
      });
    });
    return initialMutedState;
  });
  const [activeParentId, setActiveParentId] = useState<number | null>(null);
  const [isComponentVisible, setIsComponentVisible] = useState<boolean>(false);
  const progressBarRefs = useRef<{ [key: string]: HTMLDivElement | null }>({}); 
 
  const animationFrameIds = useRef<{ [key: string]: number }>({}); 
  const videoDurations = useRef<{ [key: string]: number }>({}); 
  const isMuteToggling = useRef<boolean>(false);
  const innerSwipers = useRef<any[]>([]);

  const pauseAllVideosExceptActive = (activeParentId: number | null) => {
    Object.entries(videoRefs.current).forEach(([videoKey, video]) => {
      if (video && !video.paused) {
        const [parentId] = videoKey.split('-');
        if (activeParentId === null || Number(parentId) !== activeParentId) {
          video.pause();
          if (animationFrameIds.current[videoKey]) {
            cancelAnimationFrame(animationFrameIds.current[videoKey]);
            delete animationFrameIds.current[videoKey];
          }
        }
      }
    });
  };

  const markVideoAsFinished = (parentId: number, videoIndex: number) => {
    const videoKey = `${parentId}-${videoIndex}`;
    const bar = progressBarRefs.current[videoKey];
    if (bar) {
      if (animationFrameIds.current[videoKey]) {
        cancelAnimationFrame(animationFrameIds.current[videoKey]);
        delete animationFrameIds.current[videoKey];
      }
      
      bar.style.transition = "none";
      bar.style.width = "100%";
      void bar.offsetWidth;
      bar.style.transition = "width 0.1s linear";
      
      const video = videoRefs.current[videoKey];
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    }
  };

  if (typeof window !== 'undefined' && !window.innerSwipers) {
    window.innerSwipers = [];
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting && entry.intersectionRatio > 0.2;
        setIsComponentVisible(isVisible);
        
        if (!isVisible) {
          pauseAllVideosExceptActive(null);
        } else if (activeParentId !== null) {
          pauseAllVideosExceptActive(activeParentId);
          
          const activeVideoIndex = activeVideos[activeParentId] ?? 0;
          const videoKey = `${activeParentId}-${activeVideoIndex}`;
          const video = videoRefs.current[videoKey];
          if (video && video.paused) {
            video.play().catch(console.error);
          }
        }
      },
      {
        threshold: [0, 0.5, 1],
        rootMargin: '-10% 0px -10% 0px'
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [activeParentId, activeVideos]);

  useEffect(() => {
    if (slides && slides.length > 0 && outerSwiperRef.current) {
      const firstParent = slides[0];
      setActiveParentId(firstParent.id);
      setActiveVideos(prev => ({ ...prev, [firstParent.id]: 0 }));
      
      setTimeout(() => {
        const videoKey = `${firstParent.id}-0`;
        const video = videoRefs.current[videoKey];
        if (video && isComponentVisible) {
          video.currentTime = 0;
          video.play().catch(console.error);
        }
      }, 50);
    }
  }, [slides, isComponentVisible]);

  const resetProgressBars = (parentId: number) => {
    Object.entries(progressBarRefs.current).forEach(([key, progressBar]) => {
      if (key.startsWith(`${parentId}-`) && progressBar) {
        progressBar.style.transition = 'none';
        progressBar.style.width = '0%';
        void progressBar.offsetWidth;
        progressBar.style.transition = 'width 0.1s linear';
      }
    });
  };

  const markPreviousVideosAsCompleted = (parentId: number, currentVideoIndex: number) => {
    for (let i = 0; i < currentVideoIndex; i++) {
      const videoKey = `${parentId}-${i}`;
      const progressBar = progressBarRefs.current[videoKey];
      if (progressBar) {
        progressBar.style.transition = 'none';
        progressBar.style.width = '100%';
        void progressBar.offsetWidth;
        progressBar.style.transition = 'width 0.1s linear';
      }
    }
  };

  const handleSlideChange = (swiper: any) => {
    innerSwipers.current.forEach(sw => {
      if (sw && sw.autoplay) {
        try { 
          sw.autoplay.stop(); 
        } catch (e) {}
      }
    });

    Object.entries(videoRefs.current).forEach(([videoKey, video]) => {
      if (video && !video.paused) {
        video.pause();
        if (animationFrameIds.current[videoKey]) {
          cancelAnimationFrame(animationFrameIds.current[videoKey]);
          delete animationFrameIds.current[videoKey];
        }
      }
    });
  };

  const handleSlideChangeEnd = (swiper: any) => {
    const activeSlideEl = swiper.slides[swiper.activeIndex];
    const activeOrigin = getOriginalIndexFromSlideEl(activeSlideEl);
    const activeInner = innerSwipers.current[activeOrigin || 0];
    
    if (activeInner && activeOrigin !== null) {
      const activeParent = slides?.[activeOrigin];
      if (activeParent) {
        if (activeParentId !== activeParent.id) {
          setActiveParentId(activeParent.id);
          
          pauseAllVideosExceptActive(activeParent.id);
          
          const currentVideoIndex = activeVideos[activeParent.id] ?? 0;
          activeInner.slideTo(currentVideoIndex);
          
          setTimeout(() => {
            const videoKey = `${activeParent.id}-${currentVideoIndex}`;
            const video = videoRefs.current[videoKey];
            if (video && isComponentVisible) {
              video.currentTime = 0;
              resetProgressBars(activeParent.id);
              
              if (currentVideoIndex > 0) {
                markPreviousVideosAsCompleted(activeParent.id, currentVideoIndex);
              }
              
              video.play().catch(console.error);
            }
          }, 50);
        }
      }
    }
  };

  const toggleMute = (parentId: number, videoIndex: number) => {
    const videoKey = `${parentId}-${videoIndex}`;
    const newMuteState = !isMuted[videoKey];
    setIsMuted(prev => ({ ...prev, [videoKey]: newMuteState }));
    
    isMuteToggling.current = true;
    
    const video = videoRefs.current[videoKey];
    if (video) {
      video.muted = newMuteState;
    }
    
    setTimeout(() => {
      isMuteToggling.current = false;
    }, 100);
  };

  const updateProgressBar = (videoKey: string, video: HTMLVideoElement, duration: number) => {
    if (!video || !progressBarRefs.current[videoKey]) return;
    
    const progress = (video.currentTime / duration) * 100;
    const progressBar = progressBarRefs.current[videoKey];
    
    if (progressBar) {
      const progressPercent = Math.min(100, Math.max(0, progress));
      progressBar.style.width = `${progressPercent}%`;
    }
    
    if (progress < 100 && !video.paused && !video.ended) {
      animationFrameIds.current[videoKey] = requestAnimationFrame(() => 
        updateProgressBar(videoKey, video, duration)
      );
    }
  };

  const startProgressAnimation = (videoKey: string, video: HTMLVideoElement) => {
    if (animationFrameIds.current[videoKey]) {
      cancelAnimationFrame(animationFrameIds.current[videoKey]);
      delete animationFrameIds.current[videoKey];
    }
    
    const progressBar = progressBarRefs.current[videoKey];
    
    if (progressBar && !isMuteToggling.current) {
      progressBar.style.transition = 'none';
      progressBar.style.width = '0%';
      void progressBar.offsetWidth;
      progressBar.style.transition = 'width 0.1s linear';
    }
    
    if (video.duration) {
      videoDurations.current[videoKey] = video.duration;
      updateProgressBar(videoKey, video, video.duration);
    } else {
      const onLoadedMetadata = () => {
        videoDurations.current[videoKey] = video.duration;
        updateProgressBar(videoKey, video, video.duration);
        video.removeEventListener('loadedmetadata', onLoadedMetadata);
      };
      video.addEventListener('loadedmetadata', onLoadedMetadata);
    }
  };

  const handleVideoEnd = async (parentId: number, totalVideos: number) => {
    const currentIndex = activeVideos[parentId] ?? 0;
    
    const currentVideoKey = `${parentId}-${currentIndex}`;
    const currentVideo = videoRefs.current[currentVideoKey];
    if (currentVideo) {
      currentVideo.pause();
      if (animationFrameIds.current[currentVideoKey]) {
        cancelAnimationFrame(animationFrameIds.current[currentVideoKey]);
        delete animationFrameIds.current[currentVideoKey];
      }
    }
    
    if (currentIndex < totalVideos - 1) {
      const nextIndex = currentIndex + 1;
      setActiveVideos(prev => ({ ...prev, [parentId]: nextIndex }));
      
      const videoKey = `${parentId}-${nextIndex}`;
      const video = videoRefs.current[videoKey];
      if (video && isComponentVisible) {
        video.currentTime = 0;
        video.play().catch(console.error);
        
        const parentIndex = slides.findIndex((slide: any) => slide.id === parentId);
        if (parentIndex >= 0 && innerSwipers.current[parentIndex]) {
          innerSwipers.current[parentIndex].slideTo(nextIndex);
        }
      }
      
    } else if (outerSwiperRef.current) {
      const swiper = outerSwiperRef.current;
      const currentRealIndex = swiper.realIndex;
      const nextSlideIndex = (currentRealIndex + 1) % slides.length;
      const nextParentId = slides[nextSlideIndex]?.id;
      
      if (nextParentId) {
        setActiveParentId(nextParentId);
        
        await swiper.slideNext();
        
        setActiveVideos(prev => ({
          ...prev,
          [parentId]: 0,
          [nextParentId]: 0
        }));
      }
    }
  };
  return (
    <div ref={containerRef} className="w-full">
      <Swiper
        onSwiper={(swiper) => {
          outerSwiperRef.current = swiper;
        }}
        onSlideChangeTransitionStart={handleSlideChange}
        onSlideChangeTransitionEnd={handleSlideChangeEnd}
        slidesPerView={1.4}
        spaceBetween={40}
        loop={true}
        centeredSlides={true}
        initialSlide={0}
        modules={[Navigation]}
        allowTouchMove={true}
        breakpoints={{
          565: { slidesPerView: 2, spaceBetween: 40 },
          768: { slidesPerView: 2.8, spaceBetween: 40 },
          1024: { slidesPerView: 4.2, spaceBetween: 40 },
        }}
        className="reelsSlider w-full !overflow-visible"
      >
        {slides?.map((parent: any, index: number) => {
          const activeIndex = activeVideos[parent.id] ?? 0;
          const activeVideo = parent.videos[activeIndex];

          return (
            <SwiperSlide
              key={`parent-${parent.id}`}
              data-index={index}
              className="aspect-[9/16] rounded-3xl overflow-hidden p-3 border-[1px] border-white !bg-[#ffffff3b] relative cursor-pointer"
              onClick={() => {
                if (outerSwiperRef.current && activeParentId !== parent.id) {
                  const targetIndex = slides.findIndex((slide: any) => slide.id === parent.id);
                  if (targetIndex !== -1) {
                    outerSwiperRef.current.slideToLoop(targetIndex);
                  }
                }
              }}
            >
              <div className="absolute top-7 left-0 right-0 flex gap-2 px-6 z-20">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMute(parent.id, activeIndex);
                  }}
                  className="absolute right-6 top-4 w-8 h-8 flex items-center justify-center bg-transparent rounded-full z-30"
                  aria-label={isMuted[`${parent.id}-${activeIndex}`] ? 'Unmute' : 'Mute'}
                >
                  {isMuted[`${parent.id}-${activeIndex}`] ? (
                    <svg width="25" height="25" viewBox="0 0 24 24" className="opacity-70" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 9V15H7L12 20V4L7 9H3Z" fill="#F5F5FA"/>
                      <path d="M16.5 12C16.5 10.23 15.48 8.71 14 7.97V16.02C15.48 15.29 16.5 13.77 16.5 12Z" fill="#F5F5FA"/>
                      <path d="M14 3.23V5.29C16.89 6.15 19 8.83 19 12C19 15.17 16.89 17.85 14 18.71V20.77C18.01 19.86 21 16.28 21 12C21 7.72 18.01 4.14 14 3.23Z" fill="#F5F5FA"/>
                      <path d="M3 3L21 21" stroke="#F5F5FA" strokeWidth="2"/>
                    </svg>
                  ) : (
                    <svg width="25" height="25" viewBox="0 0 24 24" className="opacity-100" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 9V15H7L12 20V4L7 9H3Z" fill="#F5F5FA"/>
                      <path d="M16.5 12C16.5 10.23 15.48 8.71 14 7.97V16.02C15.48 15.29 16.5 13.77 16.5 12Z" fill="#F5F5FA"/>
                      <path d="M14 3.23V5.29C16.89 6.15 19 8.83 19 12C19 15.17 16.89 17.85 14 18.71V20.77C18.01 19.86 21 16.28 21 12C21 7.72 18.01 4.14 14 3.23Z" fill="#F5F5FA"/>
                    </svg>
                  )}
                </button>
                {parent.videos.map((video: any, idx: number) => {
                  const isActive = idx === activeIndex;
                  const isCurrentParentActive = activeParentId === parent.id;
                  const videoKey = `${parent.id}-${idx}`;
                  
                  return (
                    <div
                      key={`bar-${video.id}`}
                      className="h-1 bg-white/30 rounded-full overflow-hidden transition-all duration-300 relative flex-1"
                      style={{ 
                        flex: isActive ? 5 : 1,
                        opacity: isCurrentParentActive ? 1 : 0.5
                      }}
                    >
                      <div
                        ref={el => {
                          if (el) {
                            progressBarRefs.current[videoKey] = el;
                            if (!el.dataset.initialized) {
                              el.style.width = '0%';
                              el.style.transition = 'width 0.1s linear';
                              el.dataset.initialized = "true";
                            }
                          }
                        }}
                        className="h-full absolute left-0 top-0 w-0"
                        style={{
                          backgroundColor: isCurrentParentActive ? '#851BED' : '#ffffff40',
                          height: '100%',
                          width: '0%',
                          transition: 'width 0.1s linear, background-color 0.3s ease',
                          willChange: 'width'
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              <div className="w-full h-full relative overflow-hidden rounded-2xl">
                <div className="w-full h-full">
                  <Swiper
                    className="w-full !h-full"
                    onSwiper={(innerSwiper) => {
                      innerSwipers.current[index] = innerSwiper;
                      if (typeof window !== 'undefined') {
                        if (!window.innerSwipers) {
                          window.innerSwipers = [];
                        }
                        window.innerSwipers[index] = innerSwiper;
                      }
                    }}
                    initialSlide={activeVideos[parent.id] ?? 0}
                    allowTouchMove={false}
                    autoHeight={true}
                    onSlideChange={(innerSwiper) => {
                      const activeVideoIndex = innerSwiper.activeIndex;
                      setActiveVideos(prev => ({ ...prev, [parent.id]: activeVideoIndex }));
                    }}
                  >
                    {parent.videos.map((video: any, videoIndex: number) => (
                      <SwiperSlide key={`video-${video.id}`}>
                        <div className="w-full h-full relative">
                          <video
                          ref={(el) => {
                            if (el) {
                              const videoKey = `${parent.id}-${videoIndex}`;
                              videoRefs.current[videoKey] = el;
                              el.muted = isMuted[videoKey] !== false;
                              
                                              if (activeParentId === parent.id && videoIndex === (activeVideos[parent.id] || 0) && isComponentVisible) {
                                const playPromise = el.play().catch(console.error);
                                if (playPromise !== undefined) {
                                  playPromise.then(() => {
                                    startProgressAnimation(videoKey, el);
                                  });
                                }
                              }
                              
                              const onLoadedMetadata = () => {
                                if (el.readyState >= 2) { 
                                  videoDurations.current[videoKey] = el.duration;
                                  if (el === videoRefs.current[`${parent.id}-${activeVideos[parent.id] || 0}`] && !el.paused) {
                                    startProgressAnimation(videoKey, el);
                                  }
                                }
                              };
                              
                              if (el.readyState >= 2) {
                                onLoadedMetadata();
                              } else {
                                el.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
                              }
                            }
                          }}
                          onPlay={(e) => {
                            const videoKey = `${parent.id}-${videoIndex}`;
                            startProgressAnimation(videoKey, e.currentTarget);
                          }}
                          onPause={(e) => {
                            const videoKey = `${parent.id}-${videoIndex}`;
                            if (animationFrameIds.current[videoKey]) {
                              cancelAnimationFrame(animationFrameIds.current[videoKey]);
                              delete animationFrameIds.current[videoKey];
                            }
                          }}
                          src={getStrapiMedia(video.media?.url)}
                          className="w-full h-full object-cover rounded-2xl"
                          playsInline
                          loop={false}
                          onEnded={() => {
                            handleVideoEnd(parent.id, parent.videos.length);
                          }}
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>

              <div className="absolute left-1/2 -bottom-1 -translate-x-1/2 -translate-y-1/2 w-[80%] lg:w-[85%] h-[9%] md:h-[11%] xl:h-[8%] bg-white rounded-xl flex items-center justify-center text-center px-2 z-20">
                <p className="text-purple m-0 font-barlow font-medium text-base lg:text-md xl:text-xl line-clamp-2">
                  {activeVideo?.heading}
                </p>
              </div>
            </SwiperSlide>
          );
        })}
        
        <button
          onClick={() => {
            if (!outerSwiperRef.current || activeParentId === null) return;
            
            const currentParent = slides?.find((slide: any) => slide.id === activeParentId);
            const currentParentIndex = slides?.findIndex((slide: any) => slide.id === activeParentId);
            
            if (!currentParent || currentParentIndex === -1) return;
            
            const currentVideoIndex = activeVideos[activeParentId] ?? 0;
            
            if (currentVideoIndex > 0) {
              const prevVideoIndex = currentVideoIndex - 1;
              const currentVideoKey = `${activeParentId}-${currentVideoIndex}`;
              const prevVideoKey = `${activeParentId}-${prevVideoIndex}`;
              
              const currentVideo = videoRefs.current[currentVideoKey];
              if (currentVideo) {
                currentVideo.pause();
                currentVideo.currentTime = 0;
                if (animationFrameIds.current[currentVideoKey]) {
                  cancelAnimationFrame(animationFrameIds.current[currentVideoKey]);
                  delete animationFrameIds.current[currentVideoKey];
                }
              }
              
              const currentProgressBar = progressBarRefs.current[currentVideoKey];
              if (currentProgressBar) {
                currentProgressBar.style.transition = 'none';
                currentProgressBar.style.width = '0%';
                void currentProgressBar.offsetWidth;
                currentProgressBar.style.transition = 'width 0.1s linear';
              }
              
              setActiveVideos(prev => ({ ...prev, [activeParentId]: prevVideoIndex }));
              
              const innerSwiper = innerSwipers.current[currentParentIndex];
              if (innerSwiper) {
                innerSwiper.slideTo(prevVideoIndex);
                
                const prevVideo = videoRefs.current[prevVideoKey];
                if (prevVideo) {
                  const prevProgressBar = progressBarRefs.current[prevVideoKey];
                  if (prevProgressBar) {
                    prevProgressBar.style.transition = 'none';
                    prevProgressBar.style.width = '0%';
                    void prevProgressBar.offsetWidth;
                    prevProgressBar.style.transition = 'width 0.1s linear';
                  }
                  
                  prevVideo.currentTime = 0;
                  if (isComponentVisible) {
                    prevVideo.play().catch(console.error);
                  }
                }
              }
            } else {
              if (outerSwiperRef.current) {
                const swiper = outerSwiperRef.current;
                const currentRealIndex = swiper.realIndex;
                const prevSlideIndex = currentRealIndex === 0 ? slides.length - 1 : currentRealIndex - 1;
                const prevParent = slides[prevSlideIndex];
                
                if (prevParent) {
                  const lastVideoIndex = (prevParent.videos?.length || 1) - 1;
                  
                  pauseAllVideosExceptActive(null);
                  
                  setActiveParentId(prevParent.id);
                  setActiveVideos(prev => ({ 
                    ...prev, 
                    [prevParent.id]: lastVideoIndex 
                  }));
                  
                  swiper.slidePrev();
                  
                  setTimeout(() => {
                    const innerSwiper = innerSwipers.current[prevSlideIndex];
                    if (innerSwiper) {
                      innerSwiper.slideTo(lastVideoIndex, 0);
                      resetProgressBars(prevParent.id);
                      if (lastVideoIndex > 0) {
                        markPreviousVideosAsCompleted(prevParent.id, lastVideoIndex);
                      }
                      
                      const videoKey = `${prevParent.id}-${lastVideoIndex}`;
                      const video = videoRefs.current[videoKey];
                      if (video && isComponentVisible) {
                        video.currentTime = 0;
                        video.play().catch(console.error);
                      }
                    }
                  }, 100);
                }
              }
            }
          }}
          className="swiper-button-prev after:!text-white hover:after:opacity-70"
          aria-label="prev"
        />

        <button
          onClick={() => {
            if (!outerSwiperRef.current || activeParentId === null) return;
            
            const currentParent = slides?.find((slide: any) => slide.id === activeParentId);
            const currentParentIndex = slides?.findIndex((slide: any) => slide.id === activeParentId);
            
            if (!currentParent || currentParentIndex === -1) return;
            
            const currentVideoIndex = activeVideos[activeParentId] ?? 0;
            const totalVideos = currentParent.videos?.length || 0;
            
            if (currentVideoIndex < totalVideos - 1) {
              const nextVideoIndex = currentVideoIndex + 1;
              const currentVideoKey = `${activeParentId}-${currentVideoIndex}`;
              const nextVideoKey = `${activeParentId}-${nextVideoIndex}`;

              const currentVideo = videoRefs.current[currentVideoKey];
              if (currentVideo) {
                currentVideo.pause();
                currentVideo.currentTime = 0;
                if (animationFrameIds.current[currentVideoKey]) {
                  cancelAnimationFrame(animationFrameIds.current[currentVideoKey]);
                  delete animationFrameIds.current[currentVideoKey];
                }
              }
              
              markVideoAsFinished(activeParentId, currentVideoIndex);
              
              setActiveVideos(prev => ({ ...prev, [activeParentId]: nextVideoIndex }));
              
              const innerSwiper = innerSwipers.current[currentParentIndex];
              if (innerSwiper) {
                innerSwiper.slideTo(nextVideoIndex);
                
                const nextVideo = videoRefs.current[nextVideoKey];
                if (nextVideo && isComponentVisible) {
                  nextVideo.currentTime = 0;
                  nextVideo.play().catch(console.error);
                }
              }
            } else {
              if (outerSwiperRef.current) {
                const swiper = outerSwiperRef.current;
                const currentRealIndex = swiper.realIndex;
                const nextSlideIndex = (currentRealIndex + 1) % slides.length;
                const nextParent = slides[nextSlideIndex];
                
                if (nextParent) {
                  pauseAllVideosExceptActive(null);
                  
                  setActiveParentId(nextParent.id);
                  setActiveVideos(prev => ({ 
                    ...prev, 
                    [nextParent.id]: 0
                  }));
                  
                  swiper.slideNext();
                  
                  setTimeout(() => {
                    const innerSwiper = innerSwipers.current[nextSlideIndex];
                    if (innerSwiper) {
                      innerSwiper.slideTo(0, 0);
                      resetProgressBars(nextParent.id);
                      
                      const videoKey = `${nextParent.id}-0`;
                      const video = videoRefs.current[videoKey];
                      if (video && isComponentVisible) {
                        video.currentTime = 0;
                        video.play().catch(console.error);
                      }
                    }
                  }, 100);
                }
              }
            }
          }}
          className="swiper-button-next after:!text-white hover:after:opacity-70"
          aria-label="next"
        />
      </Swiper>
    </div>
  );
}

